import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle, ChevronRight, ArrowUpRight } from "lucide-react";
import { company, services } from "../mock";

const Footer = () => {
  return (
    <footer className="relative bg-[#050e1d] text-white overflow-hidden">
      {/* CTA band */}
      <div className="relative border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div>
            <p className="section-label text-[#1e9cd7] text-[11px] font-bold uppercase">
              Nikomedya Turizm ile çalışın
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight leading-snug">
              Yolculuğunuzu birlikte planlayalım.
            </h2>
          </div>
          <div className="flex flex-wrap gap-4 shrink-0">
            <a
              href={company.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm"
            >
              <MessageCircle size={17} />
              Teklif Al
            </a>
            <Link
              to="/iletisim"
              className="btn-outline-light inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm"
            >
              İletişime Geçin
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <img
              src="/images/logo.webp"
              alt="Nikomedya Turizm"
              className="h-11 w-auto logo-white"
            />
            <p className="mt-6 text-sm leading-relaxed text-white/50 max-w-xs">
              1983'ten bu yana personel ve öğrenci taşımacılığında güvenin
              adresi. Her yolculukta konfor, kalite ve dakiklik.
            </p>
            <div className="mt-6 flex items-center gap-2">
              <span className="h-px w-10 bg-[#1e9cd7]" />
              <span className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-semibold">
                Est. 1983 — Kocaeli
              </span>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.25em] font-bold mb-6 text-white/40">
              Hızlı Erişim
            </h4>
            <ul className="space-y-3.5">
              {[
                { label: "Anasayfa", path: "/" },
                { label: "Hakkımızda", path: "/kurumsal/hakkimizda" },
                { label: "Hizmetlerimiz", path: "/hizmetlerimiz" },
                { label: "Araç Filomuz", path: "/arac-filomuz" },
                { label: "Haberler", path: "/haberler" },
                { label: "İletişim", path: "/iletisim" },
              ].map((l) => (
                <li key={l.path}>
                  <Link
                    to={l.path}
                    className="group flex items-center gap-1.5 text-[15px] text-white/65 hover:text-[#1e9cd7] transition-colors"
                  >
                    <ChevronRight
                      size={14}
                      className="transition-transform duration-300 group-hover:translate-x-1 text-[#1e9cd7]/60"
                    />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.25em] font-bold mb-6 text-white/40">
              Hizmetlerimiz
            </h4>
            <ul className="space-y-3.5">
              {services.map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/hizmetlerimiz#${s.id}`}
                    className="group flex items-center gap-1.5 text-[15px] text-white/65 hover:text-[#1e9cd7] transition-colors"
                  >
                    <ChevronRight
                      size={14}
                      className="transition-transform duration-300 group-hover:translate-x-1 text-[#1e9cd7]/60"
                    />
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.25em] font-bold mb-6 text-white/40">
              İletişim
            </h4>
            <ul className="space-y-4 text-[15px]">
              <li>
                <a
                  href={company.phoneHref}
                  className="flex items-start gap-3 text-white/65 hover:text-white transition-colors"
                >
                  <Phone size={17} className="text-[#1e9cd7] mt-0.5 shrink-0" />
                  {company.phone}
                </a>
              </li>
              <li>
                <a
                  href={company.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-white/65 hover:text-white transition-colors"
                >
                  <MessageCircle
                    size={17}
                    className="text-[#22c55e] mt-0.5 shrink-0"
                  />
                  {company.whatsapp}
                </a>
              </li>
              <li>
                <a
                  href={company.emailHref}
                  className="flex items-start gap-3 text-white/65 hover:text-white transition-colors"
                >
                  <Mail size={17} className="text-[#1e9cd7] mt-0.5 shrink-0" />
                  {company.email}
                </a>
              </li>
              <li>
                <a
                  href={company.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-white/65 hover:text-white transition-colors"
                >
                  <MapPin size={17} className="text-[#1e9cd7] mt-0.5 shrink-0" />
                  {company.address} — Haritada Göster
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/10 mt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/35">
            © {new Date().getFullYear()} {company.fullName}
          </p>
          <div className="flex items-center gap-7">
            <Link
              to="/kurumsal/kvkk-bilgilendirmesi"
              className="text-xs text-white/35 hover:text-white/80 transition-colors"
            >
              KVKK Bilgilendirmesi
            </Link>
            <Link
              to="/kurumsal/cerez-politikasi"
              className="text-xs text-white/35 hover:text-white/80 transition-colors"
            >
              Çerez Politikası
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
